import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {getGqlErrorMessage} from '@/api/request';
import {LoadingBar} from '@/components/LoadingBar';
import {AnalysisItem, DeliveryMerchantChartData} from '@/map/@types';
import {useDeliveryData} from '@/map/api';
import {DeliveryServiceType} from '@/map/enums';
import {Divider, FlexCenter} from '@/styled-components';
import {palette} from '@/styles';
import {humanizeNumber, withComma} from '@/utils';
import {
  Box,
  BoxGroup,
  BoxTitle,
  EmptyBox,
  SectionContainer,
  SectionInfoIcon,
  SectionTitle,
  SubText,
  ValueFormatText,
  ValueText,
} from '../StyledComponents';
import {DEVIVERY_COMPANIES} from './constants';
import {DeliveryTileToggle} from './DeliveryTileToggle';
import {InfoWindow} from './InfoWindow';

interface IProps {
  item: AnalysisItem;
}

export const Delivery = ({item}: IProps) => {
  const [openInfo, setOpenInfo] = useState<boolean>(false);

  const {error, isLoading, data: raw} = useDeliveryData(item);

  const data = useMemo<DeliveryMerchantChartData[] | undefined>(() => {
    if (raw?.merchants) {
      const merchants = raw.merchants;

      return Object.keys(merchants)
        .filter((key) => (merchants[key].transactionCount ?? 0) > 0)
        .map((name) => ({
          name,
          ...DEVIVERY_COMPANIES[name as DeliveryServiceType],
          ...raw.merchants?.[name],
        }));
    } else {
      return [];
    }
  }, [raw]);

  if (isLoading || error || !data?.length) {
    return (
      <SectionContainer>
        <SectionTitle>배달 매출</SectionTitle>
        <EmptyBox>
          {isLoading ? (
            <LoadingBar color={palette.textSecondary} />
          ) : error ? (
            <span
              dangerouslySetInnerHTML={{__html: getGqlErrorMessage(error)}}
            />
          ) : (
            '배달 매출 정보가 없습니다'
          )}
        </EmptyBox>
      </SectionContainer>
    );
  }

  const topSales = [...data].sort(
    (a, b) => (b.transactionSum ?? 0) - (a.transactionSum ?? 0)
  )[0];
  const topCount = [...data].sort(
    (a, b) => (b.transactionCount ?? 0) - (a.transactionCount ?? 0)
  )[0];

  return (
    <Root>
      <SectionTitle>
        배달 매출
        <SectionInfoIcon onClick={() => setOpenInfo(true)} />
      </SectionTitle>
      <BoxGroup>
        <Box>
          <BoxTitle>총 매출</BoxTitle>
          <FlexCenter>
            <ValueText>
              {humanizeNumber(raw?.total?.transactionSum, true)}
              <ValueFormatText>&nbsp;원</ValueFormatText>
            </ValueText>
          </FlexCenter>
          <BoxDivider />
          <SubText as="div" align="center">
            1위.{' '}
            <strong style={{color: topSales.color}}>{topSales.label}</strong>
          </SubText>
        </Box>
        <Box>
          <BoxTitle>총 건수</BoxTitle>
          <FlexCenter>
            <ValueText>
              {withComma(raw?.total?.transactionCount)}
              <ValueFormatText>&nbsp;건</ValueFormatText>
            </ValueText>
          </FlexCenter>
          <BoxDivider />
          <SubText as="div" align="center">
            1위.{' '}
            <strong style={{color: topCount.color}}>{topCount.label}</strong>
          </SubText>
        </Box>
      </BoxGroup>
      <GuestPrice>
        <SubText>객단가</SubText>
        {[...data]
          .sort(
            (a, b) =>
              (b.transactionSum ?? 0) / (b.transactionCount ?? 0) -
              (a.transactionSum ?? 0) / (a.transactionCount ?? 0)
          )
          .map((i) => {
            return (
              <SubText key={i.name} color={i.color}>
                {i.label.substring(0, 1)}&nbsp;
                {withComma(
                  Math.round(
                    (i.transactionSum ?? 0) / (i.transactionCount ?? 0)
                  )
                )}
                원
              </SubText>
            );
          })}
      </GuestPrice>
      <DeliveryTileToggle item={item} />
      {openInfo && (
        <InfoWindow type="sales" onClose={() => setOpenInfo(false)} />
      )}
    </Root>
  );
};

const Root = styled(SectionContainer)`
  ${BoxTitle} {
    margin-bottom: 4px;
  }
  ${Box} {
    padding: 4px;
  }
`;

const BoxDivider = styled(Divider)`
  margin-top: 8px;
  margin-bottom: 2px;
`;

const GuestPrice = styled(Box)`
  padding: 5px;
  margin-top: 4px;
  display: flex;
  justify-content: center;
  gap: 8px;
  ${SubText} {
    font-weight: 700;
  }
`;
